// 受注（案件）リソースの宣言。既存の order.service をそのまま呼ぶ（ブラウザ側＝本人の権限で実行）。
//
// 【業務フローの要】帳票番号は「受注番号（案件番号 0001）」の配下に見積 0001-01 が
// 枝番でぶら下がる体系。見積を作るには先に案件が必要で、無ければ AI はまず
// create_record(order) で受注を作る（2段階フロー）。受注番号は createOrder が自動採番する。
import { z } from 'zod';
import {
  fetchOrders,
  createOrder,
  updateOrder,
  type OrderCreateInput,
  type OrderUpdateInput,
} from '@modules/order/services/order.service';
import type { ID, Order } from '@shared/types';
import type { ListFilters, ResourceDescriptor, WriteResult } from './types';
import {
  MAX_ROWS,
  dateString,
  normalizeCode,
  resolveBusinessType,
  resolveCustomer,
  resolveEmployee,
  resolveUsergroup,
  toDateString,
  toTimestamp,
} from './shared';

/** 受注1件を、LLM が読む最小限の形へ射影する。 */
function toCompactOrder(o: Order) {
  return {
    code: o.code, // 案件番号（見積作成時の orderCode に使う）
    title: o.title,
    customer: o.customerSnapshot?.name ?? null,
    status: o.status,
    total: o.total,
    createdAt: toDateString(o.createdAt),
  };
}

async function list(filters: ListFilters): Promise<unknown> {
  const orders = await fetchOrders();

  const keyword = filters.keyword;
  const filtered = keyword
    ? orders.filter(
        (o) =>
          o.title.includes(keyword) ||
          o.code.includes(keyword) ||
          (o.customerSnapshot?.name ?? '').includes(keyword)
      )
    : orders;

  return {
    count: filtered.length,
    truncated: filtered.length > MAX_ROWS,
    orders: filtered.slice(0, MAX_ROWS).map(toCompactOrder),
  };
}

/** 案件番号（受注番号）で1件を引き当てる。見積の orderCode 解決にも使う。 */
export async function findOrderByCode(rawCode: string): Promise<Order | null> {
  const code = normalizeCode(rawCode);
  const orders = await fetchOrders();
  return orders.find((o) => normalizeCode(o.code) === code) ?? null;
}

async function get(rawCode: string): Promise<unknown> {
  const found = await findOrderByCode(rawCode);
  if (!found) {
    return { found: false, note: `案件番号「${rawCode}」は見つかりませんでした` };
  }
  return { found: true, order: toCompactOrder(found) };
}

// ── 書込（C-2）─────────────────────────────────────────────────

const writeCreateSchema = z.object({
  // 【原則】Firestore の ID を LLM に組ませない。名前か顧客コードを受け、ツール側で解決する
  customer: z.string().min(1, 'customer（顧客名または顧客コード）は必須'),
  title: z.string().min(1, 'title（案件名）は必須'),
  notes: z.string().optional(),
  businessType: z.string().min(1).optional(),
  assignee: z.string().min(1).optional(),
  usergroup: z.string().min(1).optional(),
  deliveryDate: dateString.optional(),
});

const writeUpdateSchema = z
  .object({
    customer: z.string().min(1).optional(),
    title: z.string().min(1).optional(),
    notes: z.string().optional(),
    businessType: z.string().min(1).optional(),
    assignee: z.string().min(1).optional(),
    usergroup: z.string().min(1).optional(),
    deliveryDate: dateString.optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'changes に変更する項目を1つ以上入れる' });

/**
 * 名前で受けたマスタ参照項目（業種・担当者・部署）と日付を、本体スキーマの
 * ID/Timestamp 項目へ解決する（create/update 共通）。
 */
async function resolveOrderRefs(input: {
  businessType?: string;
  assignee?: string;
  usergroup?: string;
  deliveryDate?: string;
}): Promise<Partial<OrderUpdateInput>> {
  const patch: Partial<OrderUpdateInput> = {};
  if (input.businessType !== undefined) patch.businessTypeId = (await resolveBusinessType(input.businessType)).id;
  if (input.assignee !== undefined) patch.assignedUserId = (await resolveEmployee(input.assignee)).id;
  if (input.usergroup !== undefined) patch.usergroupId = (await resolveUsergroup(input.usergroup)).id;
  if (input.deliveryDate !== undefined) patch.deliveryDate = toTimestamp(input.deliveryDate);
  return patch;
}

export const orderResource: ResourceDescriptor = {
  name: 'order',
  label: '受注',
  list,
  get,
  write: {
    fields: [
      { name: 'customer', label: '顧客', type: 'string', required: true, note: '顧客名または顧客コードをそのまま渡す（例 株式会社サンプル / 0001）。IDは不要' },
      { name: 'title', label: '案件名', type: 'string', required: true },
      { name: 'notes', label: '備考', type: 'string', note: '備考欄のテキスト' },
      { name: 'businessType', label: '業種', type: 'string', note: '業種名で指定（IDは不要。例 製造業）' },
      { name: 'assignee', label: '案件担当者', type: 'string', note: '担当者名で指定（IDは不要）' },
      { name: 'usergroup', label: '担当部署', type: 'string', note: '部署名で指定（IDは不要）' },
      { name: 'deliveryDate', label: '納品予定日', type: 'date', note: 'YYYY-MM-DD' },
    ],
    createSchema: writeCreateSchema,
    // 進行状態(status)・金額は帳票から自動同期されるため触らせない
    updateSchema: writeUpdateSchema,
    async create(data, userId: ID): Promise<WriteResult> {
      const input = data as z.infer<typeof writeCreateSchema>;
      // 顧客は名前/コードから解決（存在しないIDの受注を作らせない）
      const customer = await resolveCustomer(input.customer);
      // 業種・担当者・部署は名前→ID、納品予定日は YYYY-MM-DD→Timestamp へ解決する
      const refs = await resolveOrderRefs(input);
      const created = await createOrder(
        {
          customerId: customer.id,
          title: input.title,
          // 担当者は本体スキーマの必須項目（defaultなし）。指定が無ければ実行者を自動設定する
          assignedUserId: userId,
          ...(input.notes !== undefined ? { notes: input.notes } : {}),
          ...refs,
          aiPendingReview: true,
        } as OrderCreateInput,
        userId
      );
      return { id: created.id, code: created.code };
    },
    async update(code, changes, userId: ID): Promise<WriteResult | null> {
      const target = await findOrderByCode(code);
      if (!target) return null;
      const input = changes as z.infer<typeof writeUpdateSchema>;
      const customer = input.customer ? await resolveCustomer(input.customer) : null;
      const refs = await resolveOrderRefs(input);
      // 顧客変更時のスナップショット再構築は updateOrder（本体サービス）の責務
      await updateOrder(
        target.id,
        {
          ...(customer ? { customerId: customer.id } : {}),
          ...(input.title !== undefined ? { title: input.title } : {}),
          ...(input.notes !== undefined ? { notes: input.notes } : {}),
          ...refs,
          aiPendingReview: true,
        } as OrderUpdateInput,
        userId
      );
      return { id: target.id, code: target.code };
    },
    link: (id) => `/orders/${id}`,
  },
};
